import { useCallback, useEffect, useMemo, useState } from "react"
import { shallowEqual, useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"

import ConnectionLostWarning from "./ConnectionLostWarning"
import FloorOverviewContent from "./FloorOverviewContent"
import { useFloorOverviewData } from "./hooks/useFloorOverviewData"
import Totals from "./Totals"
import UnknownLocationSection from "./UnknownLocationSection"
import { getVirtualChildLocations } from "./utils"
import WearableDrawerButtons from "./WearableDrawerButtons"
import Loader from "@/components/Loader"
import Dialog from "@/components/shared/Dialog"
import LevelFilters from "@/components/shared/LevelFilters"
import SnackBar from "@/components/shared/SnackBar"
import WearablesModal from "@/components/WearablesModal"
import { Wearable, WearableModalType } from "@/components/WearablesModal/types"
import { HttpCodes, TestId } from "@/constants/globals"
import {
   CellDetailOverviewRoutes,
   InmateDetailOverviewRoutes,
} from "@/constants/routes"
import useDialog from "@/hooks/useDialog"
import useModal from "@/hooks/useModal"
import { useScannedWearable } from "@/hooks/useScannedWearable"
import { ScannerConsumerType } from "@/hooks/useScanner"
import { useUserRole } from "@/hooks/useUserRole"
import { useWearablesModal } from "@/hooks/useWearablesModal"
import { AppDispatch, RootState } from "@/store"
import {
   selectWearableAlertsByInmateId,
   triggerTestAlert,
} from "@/store/alertsSlice"
import {
   selectCellIdsWithActiveAlerts,
   selectCellTotals,
   selectSortedCellIds,
} from "@/store/cellsSlice"
import { fetchInmates, selectInmateByWearableId } from "@/store/inmatesSlice"
import { setSwapFlowScannerActive } from "@/store/scannerSlice"
import { fetchWearableSensors } from "@/store/wearableSensorsSlice"
import {
   selectWearableHasActiveAlertById,
   selectWearableLocationData,
   selectWearableRealtimeIds,
} from "@/store/wearablesRealtimeSlice"
import { selectFloorProducers } from "@/store/websocketSlice"
import { ColorVariant, Token } from "@/types/index"
import { Feature } from "@/types/user"
import "./index.scss"

const TEST_ALARM_SNACKBAR_DURATION = 6000

const FloorOverview = () => {
   const { facilityDetails, facilityLevelSelectors, loadingFacility } =
      useSelector(({ facilitiesReducer }: RootState) => facilitiesReducer)

   const { loadingClearAlert } = useSelector(
      ({ alertsReducer }: RootState) => alertsReducer
   )

   const canAccessTestAlarmTrigger = useUserRole(Feature.TEST_ALARM_TRIGGER)

   const testAlarmTriggerEnabled =
      canAccessTestAlarmTrigger &&
      localStorage.getItem(Token.TEST_ALARM_TRIGGER_ENABLED) === "true"

   const {
      isOpen: isTestAlarmDialogOpen,
      openDialog: openTestAlarmDialog,
      closeDialog: closeTestAlarmDialog,
   } = useDialog()

   const [pendingTestAlarmCellId, setPendingTestAlarmCellId] = useState<
      string | null
   >(null)

   const [testAlarmMessage, setTestAlarmMessage] = useState("")

   const inmateByWearableId = useSelector(selectInmateByWearableId)

   const locations = useMemo(() => {
      const parentLevelId = facilityLevelSelectors?.at(-1)?.activeValue
      return getVirtualChildLocations(facilityDetails?.levels, parentLevelId)
   }, [facilityDetails?.levels, facilityLevelSelectors])

   const producers = useSelector(selectFloorProducers)

   const {
      loading,
      error,
      changeCellStatus,
      handleClearAlert,
      handleClearAlertClick,
      isDialogOpen,
      closeDialog,
   } = useFloorOverviewData(producers)

   const {
      handleWearableDisconnect,
      handleModalWearableSave,
      handleModalScannedWearableSave,
   } = useWearablesModal()

   const wearableAlertsByInmateId = useSelector(selectWearableAlertsByInmateId)

   const navigate = useNavigate()
   const dispatch = useDispatch<AppDispatch>()
   const wearableIds = useSelector(selectWearableRealtimeIds)
   const {
      idsByLocationId: wearableIdsByLocationId,
      nameByWearableId: wearableLocationById,
   } = useSelector((state: RootState) =>
      selectWearableLocationData(state, locations)
   )
   const cellIds = useSelector(selectSortedCellIds)
   const { totalDefaultCells, totalCustomCells } = useSelector(
      selectCellTotals,
      shallowEqual
   )
   const cellIdsWithActiveAlerts = useSelector(selectCellIdsWithActiveAlerts)
   const wearableIdsWithActiveAlert = useSelector((state: RootState) =>
      selectWearableHasActiveAlertById(state, wearableAlertsByInmateId)
   )

   const facilityId = sessionStorage.getItem(Token.FACILITY_ID)

   const showSettingsWarning = useMemo(() => {
      const levelId = facilityLevelSelectors?.at(-1)?.activeValue
      return !loading && !loadingFacility && !!facilityId && !levelId
   }, [loading, loadingFacility, facilityId, facilityLevelSelectors])

   const {
      isOpen: isWearableModalOpen,
      openModal: openWearableModal,
      closeModal: closeWearableModal,
   } = useModal()

   const [selectedInmateId, setSelectedInmateId] = useState<string | null>(null)

   const initialModalMode = selectedInmateId
      ? WearableModalType.OPTIONS
      : WearableModalType.SCAN

   const {
      isScanning,
      scannedSensor,
      wearableModalMode,
      wearableOriginMode,
      swapTargetRef,
      swapTargetInmateId,
      isModalOpenRef,
      resetScanState,
      handleDisconnect,
   } = useScannedWearable({
      selectedInmateId,
      initialModalMode,
      consumerType: ScannerConsumerType.NEW_FLOOR_OVERVIEW,
      openWearableModal,
      onScannedWearableSave: handleModalScannedWearableSave,
      onDisconnect: handleWearableDisconnect,
   })

   isModalOpenRef.current = isWearableModalOpen

   const isSwapFlowScannerActive =
      isWearableModalOpen && wearableOriginMode === WearableModalType.OPTIONS

   useEffect(() => {
      dispatch(fetchInmates())
   }, [dispatch])

   useEffect(() => {
      dispatch(setSwapFlowScannerActive(isSwapFlowScannerActive))
   }, [dispatch, isSwapFlowScannerActive])

   useEffect(() => {
      return () => {
         dispatch(setSwapFlowScannerActive(false))
      }
   }, [dispatch])

   const locationVisibility =
      (localStorage.getItem(Token.LOCATION_VISIBILITY) ?? "true") === "true"

   const navigateToInmateDetail = useCallback(
      (wearableId: string) => {
         navigate(InmateDetailOverviewRoutes.SUMMARY.replace(":id", wearableId))
      },
      [navigate]
   )

   const navigateToCellDetail = useCallback(
      (cellId: string) => {
         navigate(CellDetailOverviewRoutes.SUMMARY.replace(":id", cellId))
      },
      [navigate]
   )

   const handleTestAlarmClick = useCallback(
      (cellId: string) => {
         setPendingTestAlarmCellId(cellId)
         openTestAlarmDialog()
      },
      [openTestAlarmDialog]
   )

   const dismissTestAlarm = useCallback(() => {
      setPendingTestAlarmCellId(null)
      closeTestAlarmDialog()
   }, [closeTestAlarmDialog])

   const confirmTestAlarm = useCallback(async () => {
      if (!pendingTestAlarmCellId) return
      dismissTestAlarm()
      try {
         await dispatch(triggerTestAlert(pendingTestAlarmCellId)).unwrap()
      } catch (error_) {
         if (error_ === HttpCodes.BAD_REQUEST) {
            setTestAlarmMessage("Could not trigger a test alarm for this cell.")
         }
      }
   }, [dispatch, dismissTestAlarm, pendingTestAlarmCellId])

   const totalWearables = useMemo(
      () =>
         Object.values(wearableIdsByLocationId).reduce(
            (total, ids) => total + ids.length,
            0
         ),
      [wearableIdsByLocationId]
   )

   const handleSwapDeviceClick = useCallback(
      (wearableId: string) => {
         const matchedInmate = inmateByWearableId[wearableId]
         swapTargetRef.current = matchedInmate?.id ?? null
         setSelectedInmateId(matchedInmate?.id ?? null)
         dispatch(fetchWearableSensors())
         openWearableModal()
      },
      [dispatch, inmateByWearableId, openWearableModal, swapTargetRef]
   )

   const handleCloseWearableModal = useCallback(() => {
      setSelectedInmateId(null)
      closeWearableModal()
      resetScanState()
   }, [closeWearableModal, resetScanState])

   const handleWearableSave = useCallback(
      (wearable: Wearable, inmateId?: string) => {
         if (!inmateId) return
         handleModalWearableSave(wearable, inmateId)
      },
      [handleModalWearableSave]
   )

   return (
      <div className="floor-overview__wrapper">
         <div className="floor-overview__header">
            <div className="floor-overview__header-left">
               <LevelFilters />
            </div>
            <div className="floor-overview__header-right">
               <Totals
                  totalDefaultCells={totalDefaultCells}
                  totalCustomCells={totalCustomCells}
                  totalWearables={totalWearables}
               />
               <WearableDrawerButtons />
            </div>
         </div>
         <ConnectionLostWarning />
         <FloorOverviewContent
            loading={loading}
            error={error}
            showSettingsWarning={showSettingsWarning}
            cellIds={cellIds}
            wearableIds={wearableIds}
            wearableLocationById={wearableLocationById}
            wearableAlertsByInmateId={wearableAlertsByInmateId}
            locationVisibility={locationVisibility}
            locations={locations}
            wearableIdsByLocationId={wearableIdsByLocationId}
            cellIdsWithActiveAlerts={cellIdsWithActiveAlerts}
            wearableIdsWithActiveAlert={wearableIdsWithActiveAlert}
            onClearAlertClick={handleClearAlertClick}
            onNavigateToCellDetail={navigateToCellDetail}
            onNavigateToInmateDetail={navigateToInmateDetail}
            onChangeCellStatus={changeCellStatus}
            onSwapDeviceClick={handleSwapDeviceClick}
            onTriggerTestAlarm={
               testAlarmTriggerEnabled ? handleTestAlarmClick : undefined
            }
         />
         <UnknownLocationSection
            onSwapDeviceClick={handleSwapDeviceClick}
            onNavigateToInmateDetail={navigateToInmateDetail}
         />

         <Dialog
            open={isDialogOpen}
            title="Please confirm fast clearance"
            buttonTitle="Clear alarm"
            handleClose={closeDialog}
            handleButtonClick={handleClearAlert}
            isDisabled={loadingClearAlert}
         />

         <Dialog
            open={isTestAlarmDialogOpen}
            title="This will raise a real alarm which will not be cleared automatically. Are you sure you want to trigger it for this cell?"
            titleTestId={TestId.TEST_ALARM_DIALOG_TITLE}
            buttonTitle="Trigger alarm"
            buttonTestId={TestId.TEST_ALARM_DIALOG_BUTTON}
            buttonColor={ColorVariant.CERISE}
            handleClose={dismissTestAlarm}
            handleButtonClick={confirmTestAlarm}
         />

         <SnackBar
            isOpen={!!testAlarmMessage}
            message={testAlarmMessage}
            duration={TEST_ALARM_SNACKBAR_DURATION}
            onClose={() => setTestAlarmMessage("")}
         />

         {isWearableModalOpen && !isScanning && (
            <WearablesModal
               mode={wearableModalMode}
               originMode={wearableOriginMode}
               openModal={isWearableModalOpen}
               handleCloseModal={handleCloseWearableModal}
               wearable={scannedSensor}
               inmateId={swapTargetInmateId ?? selectedInmateId}
               onSave={handleWearableSave}
               onSaveScanned={(inmateId) =>
                  handleModalScannedWearableSave(inmateId, scannedSensor)
               }
               onClickDisconnect={(inmateId, wearableId) =>
                  handleDisconnect(inmateId, wearableId)
               }
            />
         )}

         {isScanning && (
            <div className="floor-overview__scanning-overlay">
               <Loader fullHeight={false} />
            </div>
         )}
      </div>
   )
}

export default FloorOverview
